import { Link } from 'react-router-dom';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { ExitToAppRounded as ExitToAppIcon } from '@material-ui/icons';

import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { ADMIN_DELIVERY_ZONES_APP_PATHS } from '../../constants/delivery-zones-app-paths.constants';
import { DeliveryZoneRow, DeliveryZoneSortColumn, DeliveryZoneSortDirection } from '../../types/delivery-zone.type';
import { DeliveryZoneChargesSummary } from './DeliveryZoneChargesSummary.component';

interface DeliveryZonesTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align?: 'left' | 'right' | 'center';
  sortColumn?: DeliveryZoneSortColumn;
}

interface DeliveryZonesTableProps {
  deliveryZones: DeliveryZoneRow[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  sortBy: DeliveryZoneSortColumn;
  sortDirection: DeliveryZoneSortDirection;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (column: DeliveryZoneSortColumn) => void;
}

const TABLE_HEADERS: DeliveryZonesTableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 60 },
  { key: 'name', label: 'Nom', sortColumn: 'name', minWidth: 160 },
  { key: 'charges', label: 'Paliers', minWidth: 220 },
  { key: 'cityCount', label: 'Villes', align: 'center', minWidth: 80 },
  { key: 'status', label: 'Statut', minWidth: 100 },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

function DeliveryZonesTableHeaderCell({
  header,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  header: DeliveryZonesTableHeader;
  sortBy: DeliveryZoneSortColumn;
  sortDirection: DeliveryZoneSortDirection;
  onSortChange: (column: DeliveryZoneSortColumn) => void;
}) {
  const { sortColumn } = header;
  const isSorted = sortColumn === sortBy;

  return (
    <TableCell
      aria-label={header.ariaLabel}
      align={header.align ?? 'left'}
      sortDirection={isSorted ? sortDirection : false}
      style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
    >
      {sortColumn ? (
        <TableSortLabel
          active={isSorted}
          direction={isSorted ? sortDirection : 'asc'}
          onClick={() => onSortChange(sortColumn)}
        >
          {header.label}
        </TableSortLabel>
      ) : (
        header.label
      )}
    </TableCell>
  );
}

function DeliveryZoneTableRow({ deliveryZone }: { deliveryZone: DeliveryZoneRow }) {
  return (
    <TableRow hover tabIndex={-1}>
      <TableCell align="left">
        <IconButton
          size="small"
          aria-label="voir"
          component={Link}
          to={ADMIN_DELIVERY_ZONES_APP_PATHS.DELIVERY_ZONE_DETAILS_PATH(deliveryZone.id)}
        >
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell align="left">{deliveryZone.name}</TableCell>
      <TableCell align="left">
        <DeliveryZoneChargesSummary charges={deliveryZone.charges} />
      </TableCell>
      <TableCell align="center">{deliveryZone.cityCount}</TableCell>
      <TableCell align="left">
        <StatusChip isActive archived={deliveryZone.archivedAt !== null} />
      </TableCell>
    </TableRow>
  );
}

export function DeliveryZonesTable({
  deliveryZones,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  sortBy,
  sortDirection,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: DeliveryZonesTableProps) {
  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <DeliveryZonesTableHeaderCell
                  key={header.key}
                  header={header}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {deliveryZones.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} style={{ textAlign: 'center' }}>
                    Aucune zone de livraison trouvée
                  </TableCell>
                </TableRow>
              )}
              {deliveryZones.map((deliveryZone) => (
                <DeliveryZoneTableRow key={deliveryZone.id} deliveryZone={deliveryZone} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
